import { AppDate } from '../envTypes';

import { ModelBase } from './base';
import { BasicProfile, Profile } from './profile';
import { StoreFront } from './store';
import { Address } from './address';
import { PayoutBank, PayoutVenmo, PayoutPaypal, PaymentType } from './payment';
import { PopStoreThemeTemplate } from './theme-template';
import {
  PackageTemplateUnit,
  PackageTemplate,
  InsurancePolicy,
  USPSShippingServiceLevel,
  SignatureConfirmationType,
} from './shipping';
import { PopStoreExtendedSubscriptionLevel } from './creator-subscription';
import { MsmStatus, PaymentMethodDetails } from './order';
import type { VisibilityType } from './shared';
import type { AppSellerApplicationPurpose } from './seller-application';
import type { EchoPreference } from './echo-preference';
import type { RealEstateGoal } from './real-estate-goal';
import { PopStoreCreatorOnboardingContest } from './onboarding-contest';
import {
  SellerOnboardingStepState,
  SellerOnboardingStepType,
} from './seller-onboarding';
import { WeeklyContestInfo } from './weekly-contest';
import { AnsweredLeadFormQuestion } from './lead-form';

export interface DeviceToken {
  token: string;
  registerTime: AppDate;
}
export enum BodyType {
  Lean = 'Lean',
  Normal = 'Normal',
  BBW = 'BBW',
  Toned = 'Toned',
  Muscular = 'Muscular',
  BodyBuilder = 'Body Builder',
}

export enum SkinComplexion {
  Pale = 'Pale',
  Fair = 'Fair',
  Olive = 'Olive',
  Dark = 'Dark',
}

export enum HeightUnit {
  'cm' = 'cm',
  'in' = 'in',
  'ft' = 'ft',
  'm' = 'm',
}

export enum BwhUnit {
  'cm' = 'cm',
  'in' = 'in',
}

export enum DressSize {
  'XS' = 'XS',
  'S' = 'S',
  'M' = 'M',
  'L' = 'L',
  'XL' = 'XL',
  'XXL+' = 'XXL+',
}

export enum Ethnicity {
  Asian = 'Asian',
  Caucasian = 'Caucasian',
  European = 'European',
  MiddleEastern = 'Middle Eastern',
  Indian = 'Indian',
  Hispanic = 'Hispanic',
  African = 'African',
  Brazilian = 'Brazilian',
}

export enum HairColor {
  Blonde = 'Blonde',
  Brunette = 'Brunette',
  Red = 'Red Head',
  Black = 'Black hair',
  White = 'White hair',
}

export enum HairLength {
  Short = 'Short',
  Medium = 'Medium',
  Long = 'Long',
  VeryLong = 'Very Long',
}

export enum HairStyle {
  Straight = 'Straight',
  Braided = 'Braided',
  Dreadlocks = 'Dreadlocks',
  Curly = 'Curly',
  WavyHair = 'Wavy Hair',
}

/**
 * Which dashboard shell the creator is currently in.
 *
 * Only the ECHO-ME and POP.STORE shells sit on this axis — a creator
 * crosses between them from the shell-switch row each sidebar carries
 * (ECHO-ME's "My POP.STORE" and POP.STORE's "AI ECHO-ME Agents"), and
 * the sidebar, mobile drawer, bottom bar and dashboard home all follow.
 *
 * Real-estate creators are OUTSIDE this axis: their shell carries no
 * switch row, so nothing ever reads this field for them.
 *
 * Absent means the creator has never switched, and the client derives
 * the shell from their signup signals instead.
 */
export enum PopStoreCreatorDashboardShellMode {
  Echo = 'echo',
  Generic = 'generic',
}

export enum CustomOnboardingAction {
  PrioritizeAIEcho = 'onboarding_prioritize_ai_echo',
  PrioritizeCreateCourse = 'onboarding_prioritize_create_course',
  PrioritizeConfigureCommunity = 'onboarding_prioritize_configure_community',
  PrioritizeCreateVideoSeries = 'onboarding_prioritize_create_video_series',
  PrioritizeGoLive = 'onboarding_prioritize_go_live',
  PrioritizeAddDigitalProduct = 'onboarding_prioritize_add_digital_product',
  PrioritizeSubscription = 'onboarding_subscriptions',
  PrioritizeTips = 'onboarding_tips',
}

export enum EyeColor {
  Brown = 'Brown',
  Blue = 'Blue',
  Hazel = 'Hazel',
  Green = 'Green',
}

export enum Gender {
  Male = 'male',
  Female = 'female',
}

export enum GenderV2 {
  Male = 'm',
  Female = 'f',
}

export enum AIExtractedGender {
  Masculine = 'masculine',
  Feminine = 'feminine',
  Androgynous = 'androgynous',
}

export enum ModelMePersonalImageType {
  Face = 'face',
  Body = 'body',
  Tattoo = 'tattoo',
}

export enum ModelMePersonalImageExt {
  jpeg = 'jpeg',
  jpg = 'jpg',
  png = 'png',
}

export enum BreastImplants {
  Normal = 'Normal',
  Large = 'Large',
  ExtraLarge = 'Extra Large',
}

export interface PersonalLook extends ModelBase {
  isDefault: boolean;
  name: string;
  shopId: string;
  creatorId: string;

  faceImageAwsS3Urls: string[]; // required at least 1 for avatarme
  faceImageAwsS3UrlsV2?: {
    front: string;
    // left and right must either both be present or both be absent
    left?: string;
    right?: string;
  };
  bodyImageAwsS3UrlsV2: {
    front: string;
    back: string;
    profile: string;
  }; // required for avatarme
  bodyType: BodyType; // required for avatarme
  skinComplexion: SkinComplexion; // required for avatarme
  eyeColor: EyeColor; // required for avatarme
  gender: Gender; // required for avatarme

  tattooImageAwsS3Urls?: {
    front: string;
    closeup: string;
  }; // optional for avatarme
  brazilianButtLift?: boolean; // optional for avatarme
  breastImplants?: BreastImplants; // optional for avatarme

  bodyImageAwsS3Urls?: string[];
  makeItMoreMe?: boolean;

  ethnicity?: Ethnicity; // optional for avatarme
  hairColor?: HairColor; // optional for avatarme
  hairLength?: HairLength; // optional for avatarme
  hairStyle?: HairStyle; // optional for avatarme
  dressSize?: DressSize; // optional for avatarme
  height?: number; // optional for avatarme
  heightUnit?: HeightUnit; // optional for avatarme
  bustSize?: number; // optional for avatarme
  bustUnit?: BwhUnit; // optional for avatarme
  waistSize?: number; // optional for avatarme
  waistUnit?: BwhUnit; // optional for avatarme
  hipSize?: number; // optional for avatarme
  hipUnit?: BwhUnit; // optional for avatarme
  lipFillers?: boolean; // optional for avatarme
  cheekboneFillers?: boolean; // optional for avatarme
  chinFillers?: boolean; // optional for avatarme
  definedAbs?: boolean; // optional for avatarme
}

// Auth-related types
export interface FacebookAuthResponse {
  accessToken: string | undefined;
  data_access_expiration_time: number | undefined;
  expiresIn: number;
  signedRequest: string | undefined;
  userID: string;
  grantedScopes: string | undefined;
  reauthorize_required_in?: number | undefined;
  code?: string | undefined;
}

export enum CommentChatConnectionStatus {
  Connecting = 'connecting',
  Authorized = 'authorized',
  Success = 'success',
  Error = 'error',
}

export enum GoogleOauth2ConnectionStatus {
  Authorized = 'authorized',
  // need to re-authorize
  Expired = 'expired',
  Error = 'error',
}

export interface CommentChatAuthorization {
  lastAuthorizedAt: AppDate;
  authResponse: FacebookAuthResponse;
}

export interface CommentChatConnection {
  id: string;
  extId: string;
  platform: 'fb' | 'ig';
  name: string;
  accessToken: string;
  createdAt: number;
  updatedAt: number;
}

export enum CommentCommunityChannelType {
  FbPage = 'fb_page',
  IgAccount = 'ig_account',
}

export interface CommentCommunityChannel {
  // The channel type.
  type: CommentCommunityChannelType;

  // The channel ID
  id: string;

  // The connection ID
  connectionId: string;

  // The external channel ID
  extId: string;

  // The channel name
  name: string;

  // The channel access token
  accessToken?: string;

  // Whether the channel is subscribed to meta webhooks
  subscribedToMetaWebhooks?: boolean;

  // Profile picture URL.
  profilePictureUrl?: string;
}

// Stripe and onboarding types
export interface StripeCapabilityRequirementError {
  code: string;
  detailedCode?: string;
  requirement: string;
  reason: string;
}

export enum PostOnboardedStep {
  ENABLE_SUBSCRIPTION = 'enable-subscription',
  ENABLE_TIPPING = 'enable-tipping',
  ADD_BUTTON = 'add-link-button',
  ADD_AFFILIATE_PRODUCT = 'add-affiliate-product',
  ADD_MEETING_SLOT = 'add-meeting-slot',
  PRIORITIZE_AI_ECHO = CustomOnboardingAction.PrioritizeAIEcho,
  PRIORITIZE_CREATE_COURSE = CustomOnboardingAction.PrioritizeCreateCourse,
  PRIORITIZE_CONFIGURE_COMMUNITY = CustomOnboardingAction.PrioritizeConfigureCommunity,
  PRIORITIZE_CREATE_VIDEO_SERIES = CustomOnboardingAction.PrioritizeCreateVideoSeries,
  PRIORITIZE_LIVE_VIDEO = CustomOnboardingAction.PrioritizeGoLive,
  PRIORITIZE_CREATE_DIGITAL_PRODUCT = CustomOnboardingAction.PrioritizeAddDigitalProduct,
  PRIORITIZE_ONBOARDING_SUBSCRIPTION = CustomOnboardingAction.PrioritizeSubscription,
  PRIORITIZE_ONBOARDING_TIPS = CustomOnboardingAction.PrioritizeTips,
}

export type PopStoreCreatorOnboardedStepsInfo = {
  step: PostOnboardedStep;
  isSkipped: boolean;
  isCompleted: boolean;
};

// ── Express-Setup wizard persistence ─────────────────────────────────────
//
// `expressSetup` on `PrivateProfile` holds the full state of the
// post-signup wizard. Each step records its own `ExpressSetupStepState`
// (skipped vs completed + timestamps) so resume + analytics can tell
// apart "user reached this step" from "user actually filled it in".
//
// Step-specific sub-state lives on dedicated optional sub-fields (e.g.
// `template`, `leadMagnet`) — kept normalized rather than stuffing a
// `Record<string, unknown>` so consumers (admin tools, analytics
// pipelines) can read it without reaching for `as any`.

/**
 * Canonical step identifiers — string values are also the URL slugs.
 *
 * Not every creator sees every step: the client owns per-flow step ORDERS
 * (see `ExpressSetupFlow` in `@ps/features/express-setup`), e.g. real-estate
 * creators run goals → template → import-listings → calendar → lead-form →
 * live.
 */
export enum ExpressSetupStep {
  Welcome = 'welcome',
  Goals = 'goals',
  Template = 'template',
  ImportListings = 'import-listings',
  Calendar = 'calendar',
  ContactCard = 'contact-card',
  LeadForm = 'lead-form',
  LeadMagnet = 'lead-magnet',
  Live = 'live',
}

/** Per-step status. `pending` = reached but not acted on. */
export enum ExpressSetupStepStatus {
  Pending = 'pending',
  Completed = 'completed',
  Skipped = 'skipped',
}

export interface ExpressSetupStepState {
  status: ExpressSetupStepStatus;
  /** First time the user reached this step (ms since epoch). */
  enteredAt?: number;
  /** When the step was marked completed or skipped (ms since epoch). */
  resolvedAt?: number;
  /** How many times the user has revisited this step. */
  visitCount?: number;
}

/**
 * Which Template-step path the user chose. `template-only` = picked a
 * pre-made template; `ai-customized` = ran Customize-with-AI on top.
 */
export enum ExpressSetupTemplateChoice {
  TemplateOnly = 'template-only',
  AiCustomized = 'ai-customized',
}

/**
 * Generic theme-styling footprint, decoupled from the express-setup flow.
 *
 * Backfilled once (the first time the user lands on the Style screen) so the
 * Customize-with-AI socket run has a stable `sessionId` without re-reading the
 * express-setup template state every time:
 *   • `activeSessionId` — the AI socket session id. Backfilled from the
 *     express-setup template's `aiSessionId` when present, else the creator's
 *     public profile id.
 *   • `activeTemplateId` — the template last applied via "Use this style".
 *   • `choice` — which style path the user took (template-only vs ai-customized).
 */
export interface ThemeSettings {
  activeSessionId?: string;
  activeTemplateId?: string;
  choice?: ExpressSetupTemplateChoice;
}

export interface ExpressSetupTemplateState {
  choice?: ExpressSetupTemplateChoice;
  selectedTemplateId?: string;
  /** Free-text prompt the user submitted in the Customize-with-AI modal. */
  aiPrompt?: string;
  /** Whether a headshot was uploaded for the AI flow. */
  aiHeadshotUploaded?: boolean;
  /**
   * UUID we generated for the customize-with-AI socket run and sent as
   * `sessionId` in phases 1 & 2. Persisted as a footprint so we can
   * correlate Firestore state with backend logs after the fact.
   */
  aiSessionId?: string;
  /**
   * Whether the template selection finished successfully:
   *   • `TemplateOnly`  — true the moment the user taps "Use this style".
   *   • `AiCustomized` — true only after the socket emits
   *     `customization_compilation_success` and we've applied the
   *     resulting customization.
   * Lets the resume-redirect logic distinguish "stepped through" from
   * "actually finished" and skip the AI loader for users who already
   * completed the step.
   */
  completed?: boolean;
}

/** Real-estate flow: outcome of the "import your listings" step. */
export interface ExpressSetupImportListingsState {
  /** Listing ids created from the pasted portal URLs. */
  importedListingIds?: string[];
  /** URLs the scrape could not read (partial-success footprint). */
  failedUrls?: string[];
}

/** Real-estate flow: outcome of the Google-Calendar connect step. */
export interface ExpressSetupCalendarState {
  /** True once `googleCalendar.status` read as authorized on this step. */
  connected?: boolean;
}

export interface ExpressSetupContactCardState {
  /** True once the contact card was saved at least once. */
  saved?: boolean;
}

export interface ExpressSetupLeadFormState {
  /** True once the lead-form config was saved at least once. */
  saved?: boolean;
  /**
   * True once the `simple-lead-capture` custom button was added to the
   * bio's customization. Set on the "Add and continue" press; missing
   * or false means the bio still needs the button (e.g. helper failed
   * or user dedup-skipped because one already existed).
   */
  customButtonAppended?: boolean;
}

/**
 * Which Lead-Magnet sub-flow the user took.
 * - `upload` = user uploaded their own PDF
 * - `ai-generated` = user picked a template and ECHO generated a guide
 */
export enum ExpressSetupLeadMagnetPath {
  Upload = 'upload',
  AiGenerated = 'ai-generated',
}

export interface ExpressSetupLeadMagnetState {
  path?: ExpressSetupLeadMagnetPath;
  /** AI-flow only: which template the user picked from the 2x2 grid. */
  aiTemplateId?: string;
  /** AI-flow only: data captured in the "Make it yours" form. */
  makeItYours?: {
    market?: string;
    /** Single audience value selected from the chip row. */
    audience?: string;
    /** Single CTA value selected from the chip row. */
    actions?: string;
  };
  /**
   * AI-flow only: content-generator session id assigned client-side when
   * the user taps "Generate Guide". Lets a page refresh resume polling
   * via `GET /pdf-generation-status/{sessionId}` instead of restarting
   * the generation. The backend creates the digital-product listing as
   * part of the generation flow, so the client does NOT need to persist
   * the PDF URL, cover image, or description — only the session id.
   */
  aiSessionId?: string;
  /** Listing id of the digital product created at the end of the flow. */
  digitalProductListingId?: string;
  /** True once we appended the digital-product button to the bio. */
  customButtonAppended?: boolean;
}

/** Creator's niche from signup. `slug` is absent when inference skipped the picker. */
export interface CreatorNiche {
  slug?: string;
  label: string;
}

export interface ExpressSetupProgress {
  /** Slug of the step the user is currently on (or last persisted). */
  currentStep: ExpressSetupStep;
  /** Per-step state map. Keyed by `ExpressSetupStep`. */
  steps?: { [P in ExpressSetupStep]?: ExpressSetupStepState };
  /** Step-specific sub-state. */
  template?: ExpressSetupTemplateState;
  importListings?: ExpressSetupImportListingsState;
  calendar?: ExpressSetupCalendarState;
  contactCard?: ExpressSetupContactCardState;
  leadForm?: ExpressSetupLeadFormState;
  leadMagnet?: ExpressSetupLeadMagnetState;
  /** True once the user landed on the Live step. Drives resume redirects. */
  finished?: boolean;
  /** When `finished` flipped to true (ms since epoch). */
  finishedAt?: number;
  /**
   * Footprint that this creator went through the SHORTENED setup (Template
   * step only) rather than the full express-setup flow — set for real-estate
   * creators when `realestate_express_setup_template_only` is enabled. Lets
   * admin/analytics distinguish a short setup from one that completed every
   * step. The remaining steps are intentionally absent from `steps`, not
   * skipped-by-user.
   */
  shortSetup?: boolean;
  /** ms-since-epoch of the most recent write. */
  updatedAt?: number;
}

// ────────────────────────────────────────────────────────────────────
// AI Profile Builder progress
//
// Per-step persistence for the AI Profile Builder wizard, mirroring the
// express-setup shape above. Stored under
// `privateProfiles/{uid}.aiProfileBuilderSetting.progress` so the flow can
// resume after a refresh or on a different device. Reuses
// `ExpressSetupStepState` / `ExpressSetupStepStatus` for the per-step map.

/**
 * Canonical step ids — string values double as URL path segments.
 *
 * Note: there is no `Headshot` step. The headshot is picked inline on the
 * prompt page (and the template preview), not on a dedicated route, so it's
 * persisted as `AiProfileBuilderProgress.headshot` sub-state rather than a
 * routable step.
 */
export enum AiProfileBuilderStep {
  Prompt = 'prompt',
  Goals = 'goals',
  Processing = 'processing',
  Preview = 'preview',
}

/** Mirrors the WS context's `ProfileBuilderFlowType`. */
export type AiProfileBuilderFlowType = 'normal' | 'refine' | 'template';

export interface AiProfileBuilderPromptState {
  prompt?: string;
  templateId?: string | null;
  flowType?: AiProfileBuilderFlowType;
}

export interface AiProfileBuilderHeadshotState {
  /** S3 key of the uploaded headshot (the WS `formData.headshot`). */
  s3Key?: string;
  /** True when the user skipped the (optional) headshot. */
  skipped?: boolean;
  /** When the headshot was uploaded (ms since epoch). */
  uploadedAt?: number;
}

export interface AiProfileBuilderGoalsState {
  selectedGoals?: string[];
}

export interface AiProfileBuilderProgress {
  /** Slug of the step the user is currently on (or last persisted). */
  currentStep: AiProfileBuilderStep;
  /** Per-step state map. Keyed by `AiProfileBuilderStep`. */
  steps?: { [P in AiProfileBuilderStep]?: ExpressSetupStepState };
  /** Step-specific sub-state. */
  prompt?: AiProfileBuilderPromptState;
  headshot?: AiProfileBuilderHeadshotState;
  goals?: AiProfileBuilderGoalsState;
  /** Populated once the server produces them (poll result / preview reached). */
  customizationUrl?: string;
  coverImageUrl?: string;
  /** The sessionId (=== uid) the run used. Footprint for cross-device correlation. */
  sessionId?: string;
  /**
   * True while phase-1 generation is in flight (from prompt submit until the
   * flow advances past the prompt step). Lets a refresh on the prompt step
   * detect an in-flight build and resume it (via `isResumeFlow`) instead of
   * restarting. Cleared when the flow advances or generation resolves.
   */
  generating?: boolean;
  /**
   * True once the build completed and the user reached the bio preview.
   * Distinct from `aiProfileBuilderSetting.built`, which RN sets only after
   * the user applies the style. Resume-to-preview = `finished && !built`.
   */
  finished?: boolean;
  /** When `finished` flipped to true (ms since epoch). */
  finishedAt?: number;
  /** ms-since-epoch of the most recent write. */
  updatedAt?: number;
}

export type PopStoreCreatorOnboardedInfo = {
  currentStep?: PostOnboardedStep;
  steps?: PopStoreCreatorOnboardedStepsInfo[];
  isCompleted?: boolean;
  isNewUser?: boolean;
  viewedAllStep?: boolean;
  isEmptySocialState?: boolean;
  followersCount?: number;
  isPopstoreUrlAttached?: boolean;
  linkInBioFetchCompleted?: boolean;
  socialLinksImported?: boolean;
};

export interface PrivateProfile {
  birthday?: AppDate;
  profilePictureConfirmed?: boolean;
  storeCoverPhotoConfirmed?: boolean;
  storeInfoConfirmed?: boolean;
  businessLocationConfirmed?: boolean;
  shippingOptionsConfirmed?: boolean;
  sellerOnboardingCompleted?: boolean;
  sellerOnboardingCompletedBy?: 'seller' | 'admin';
  sellerOnboardingCompletedAt?: AppDate;
  isSellerFirstTimeWalkThroughPresented?: boolean;
  defaultPayment?: string;
  defaultPaymentType?: PaymentType;
  defaultAddress?: Address | null;
  extraAddresses?: Address[] | null;
  sellerShippingAddress?: Address;
  payoutMethodBank?: PayoutBank;
  payoutMethodVenmo?: PayoutVenmo;
  payoutMethodPaypal?: PayoutPaypal;
  billingCountryCode?: string;
  stripeBillingCountryCode?: string;
  purposes?: AppSellerApplicationPurpose[];
  /** Answers to the AIEcho signup funnel's "quick question" step. */
  echoPreferences?: EchoPreference[];
  /** Answers to the real-estate signup funnel's "goals" step. */
  realEstateGoals?: RealEstateGoal[];
  /**
   * store level default tax code for products
   */
  defaultProductTaxCode?: string;

  /**
   * @deprecated replaced by `LoginSession` since 3.23.0
   */
  deviceTokens?: DeviceToken[];
  /**
   * @deprecated replaced by `LoginSession` since 3.23.0
   */
  pushNotificationTokens?: string[];
  /**
   * @deprecated replaced by `withdrawableBalance` in readonlyProfile
   */
  balance?: number;
  /**
   * move from profile
   */
  lastName?: string;
  /**
   * move from profile
   */
  email?: string;

  unsubscribe?: boolean;
  watchPasscodes?: string[];
  watchPasscodeCount?: number;
  referrerUserId?: string;
  referrerUserProfile?: Profile;
  referrerManuallyModifiedAt?: AppDate;
  referrerStore?: StoreFront;
  defaultVisibility?: VisibilityType;
  createdAt?: AppDate;

  notificationWatchedShowStart?: boolean;
  notificationAddedToWatched?: boolean;
  notificationFollowedUserStarted?: boolean;
  notificationYouAreFollowed?: boolean;
  notificationUpcomingShowDidCreate?: boolean;
  notificationUpcomingShowComment?: boolean;
  notification2WayStreamStarted?: boolean;
  notificationYouAreMentioned?: boolean;
  allowSMS?: boolean;
  /**
   * Opt-in to marketing email and product updates. Captured at creator signup
   * (the optional checkbox under the 18+ confirmation) and written either way,
   * so `false` means "declined", not "never asked" — absent means the account
   * predates the checkbox.
   */
  allowMarketingEmails?: boolean;
  /**
   * used to count how many new orders have been created after this date.
   *
   * if null, use start of today as default
   */
  startDateToCountNewOrders?: AppDate;

  notificationSetting?: string;
  timezone?: string;

  printFormat?: {
    packingSlip?: 'letter' | 'a6';
    shippingLabel?: 'a6' | 'PDF_SINGLE_8X11';
  };

  emailTags?: string[];

  /**
   * allow seller to see the upcoming and livestreaming show on web
   */
  shouldStartShowByWeb?: boolean;

  /**
   * Merchant Code, 4 digets codes.
   * codes list: https://github.com/greggles/mcc-codes/blob/main/mcc_codes.json
   * If the code is not set, it should be treated as `5262, Marketplaces`
   */
  mccCode?: string;

  iOSTrackingTransparencySetting?: string;

  orderFulfillmentSetting?: {
    /**
     * deprecated since 12/06/22
     */
    insurancePolicy?: InsurancePolicy;
    packageTemplateUnit?: PackageTemplateUnit;
    defaultPackageTemplate?: PackageTemplate & { id: string };
    /**
     * deprecated since 12/06/22
     */
    defaultShippingServiceLevel?: USPSShippingServiceLevel;
    showConfirmationWhenGeneratingMultipleLabels?: boolean;
    showConfirmationWhenRegeneratingMultipleLabels?: boolean;
    /**
     * deprecated since 12/06/22
     */
    extra?: {
      signatureConfirmation?: SignatureConfirmationType;
    };
  };

  stripeAccountId?: string;

  stripeAccountVerificationStatus?: 'verified' | 'unverified' | 'pending';

  /** for withdraw from connect account */
  stripeBankAccountId?: string;

  /** PSLPay connection and related information */
  pslPay?: {
    /** If seller has connected (i.e., signed up) PSLPay */
    connected: boolean;
    /** PSLPay sign up (connect) update timestamp */
    updatedAt: AppDate;
  };

  popStorePay?: {
    /** If creator has connected (i.e., signed up) Pop.Store Pay, enable transfer and payout ability */
    connected: boolean;
    /** Pop.Store Pay sign up (connect) update timestamp */
    updatedAt: AppDate;
  };

  /**
   * stripe connect account capability details, included charge and payout status and requirement
   */
  stripeAccountCapability?: {
    status: 'rejected' | 'pending' | 'completed' | 'enabled' | 'restricted';
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    requirement?: {
      /** Date by which the fields in currentlyDue must be collected to keep the capability enabled. */
      currentDeadline?: AppDate;
      /** need to be collected to keep the capability enabled */
      currentlyDue?: string[];
      eventuallyDue?: string[];
      /** Fields that weren't collected by currentlyDue */
      pastDue?: string[];
      disabledReason?: string;
      errors?: StripeCapabilityRequirementError[];
    };
    futureRequirement?: {
      currentDeadline?: AppDate;
      currentlyDue?: string[];
      eventuallyDue?: string[];
      pastDue?: string[];
      disabledReason?: string;
      errors?: StripeCapabilityRequirementError[];
    };
    eventUpdateAt: AppDate;
  };

  /** Seller onboarding state and statistics information */
  sellerOnboardingState?: {
    [p in SellerOnboardingStepType]?: SellerOnboardingStepState;
  };

  /** commentchat related state */
  commentchat?: {
    connectionStatus?: CommentChatConnectionStatus;
    authorization?: CommentChatAuthorization;
    connections?: CommentChatConnection[];
    channels?: CommentCommunityChannel[];
    onboardingFinished?: boolean;
    onboardingFinishedAt?: AppDate;
    waitingForAppSetup?: boolean;
    appSetupFinished?: boolean;
  };

  setupPriorityInbox?: {
    setupFinishedAt?: AppDate;
    isSetupFinished?: boolean;
    enabledMessageAssistantAt?: AppDate;
    enabledMessageAssistant?: boolean;
    autoResponderMessage?: string;
    setupStepCompleted?: number;
    setupUpdatedAt?: AppDate;
    isSubscriptionLevelSetup?: boolean;
  };

  // MSM(myShipManager)
  myShipManager?: {
    encryptedData: string;
    status: MsmStatus;
    scopes: string[];
    // redirect to MSM after approved authorization by CS API
    approveUrl: string;
    // redirect to MSM after deny authorization by CS API
    denyUrl: string;
    // shopify ID
    externalShopId: string;
    createdAt: AppDate;
    updatedAt: AppDate;
  };
  /**
   * @deprecated by defaultPersonalLookId
   * @description personal data for modelme and avatarme
   */
  modelMePersonalData?: {
    shopId?: string;
    creatorId?: string;
    faceImageAwsS3Urls: string[]; // required at least 3 for modelme, required at least 1 for avatarme
    faceImageAwsS3UrlsV2?: {
      front: string;
      // left and right must either both be present or both be absent
      left?: string;
      right?: string;
    };
    bodyImageAwsS3Urls?: string[]; // required at least 5 for modelme, optional for avatarme
    bodyImageAwsS3UrlsV2?: {
      front: string;
      back: string;
      profile: string;
    };
    dressSize: DressSize;
    ethnicity: Ethnicity;
    hairColor: HairColor;
    hairLength?: HairLength; // optional for modelme, required for avatarme
    hairStyle?: HairStyle; // optional for modelme, required for avatarme
    gender?: Gender; // optional for modelme, required for avatarme
    makeItMoreMe?: boolean; // 'Make it More Me' switch for avatarme
    bustSize?: number; // required when 'Make it More Me' switch is on
    bustUnit?: BwhUnit; // required when 'Make it More Me' switch is on
    waistSize?: number; // required when 'Make it More Me' switch is on
    waistUnit?: BwhUnit; // required when 'Make it More Me' switch is on
    hipSize?: number; // required when 'Make it More Me' switch is on
    hipUnit?: BwhUnit; // required when 'Make it More Me' switch is on
    eyeColor?: EyeColor; // optional for modelme, required when 'Make it More Me' switch is on for avatarme
    height?: number; // required for modelme, required when 'Make it More Me' switch is on for avatarme
    heightUnit?: HeightUnit; // required for modelme, required when 'Make it More Me' switch is on for avatarme
    skinComplexion?: SkinComplexion; // required when 'Make it More Me' switch is on
    definedAbs?: boolean; // optional when 'Make it More Me' switch is on
    bodyType?: BodyType; // optional when 'Make it More Me' switch is on
    lipFillers?: boolean; // required when 'Make it More Me' switch is on
    cheekboneFillers?: boolean; // required when 'Make it More Me' switch is on
    chinFillers?: boolean; // required when 'Make it More Me' switch is on
  };
  // commentsold avatarme shop id
  csAvatarMeShopId?: string;
  // a creator may have multiple personal looks, but have only one as default
  defaultPersonalLookId?: string;
  /**
   * @deprecated
   */
  avatarMeOutfitIds?: string[];
  /**
   * @deprecated
   */
  avatarMeListing?: {
    id: string;
    imageVariantId?: string;
    photoshootVariantId?: string;
    video5sVariantId?: string;
    video10sVariantId?: string;
  };
  // avatarme prices set by sellers for customers
  avatarMePrices?: {
    image?: number;
    photoshoot?: number;
    video5s?: number;
    video10s?: number;
  };
  // Open Graph description for avatarme
  avatarMeOgDesc?: string;
  googleCalendar?: {
    calendarId?: string;
    status: GoogleOauth2ConnectionStatus;
    redirectUri?: string;
    tokens?: {
      accessToken: string;
      refreshToken: string;
      scope: string;
      expiryDate: AppDate;
    };
  };

  popStoreNotification?: {
    phone?: string;
    email?: string;
  };
  sellerApplicationRegisterIp?: string;
  popStoreDropshipApplicationId?: string;
  popStoreOnboardingContestSubmissionId?: string;
  // popstore creator onboarding contest data
  popStoreOnboardingContest?: PopStoreCreatorOnboardingContest;
  // weekly contest data
  weeklyContestInfo?: WeeklyContestInfo;
  popStoreThemePickerPresented?: boolean;
  aiProfileBuilderSetting?: {
    built?: boolean;
    /** True while a `/template-picker` apply is in flight; `built` lands only at the end of it. */
    isTemplateApplying?: boolean;
    bannerDemoted?: boolean;
    homeVisitCount?: number;
    /** Per-step wizard progress for resume. See `AiProfileBuilderProgress`. */
    progress?: AiProfileBuilderProgress;
  };
  lastCompletedDmSetupStep?: number;
  isAgeCertified?: boolean;
  avatarMeFirstVisitedAt?: AppDate;
  isNewOnboardedUser?: boolean;
  creatorCoachWelcomeVideoShown?: boolean;
  /**
   * The ECHO home's introduction is done with: collapsed by the creator, or
   * an agent was set up. Once true it shows COLLAPSED on every device — a
   * "Welcome" bar with "Read more" that opens it again — never removed. The
   * name predates the collapse; it is kept so stored values still apply.
   */
  echoHomeIntroDismissed?: boolean;
  popStoreOnboradedInfo?: PopStoreCreatorOnboardedInfo;
  /** Stamped at onboarding; absent on accounts predating the setup home. */
  isNewRealEstateUser?: boolean;
  /** Stamped on each enable of the real-estate event banner; resets it. */
  realEstateEventBannerEnabledAt?: AppDate;
  /** Post-signup express-setup wizard state. See types above. */
  expressSetup?: ExpressSetupProgress;
  /** Generic theme-styling footprint. See `ThemeSettings`. */
  themeSettings?: ThemeSettings;
  sessionId?: string;
  customOnboardingActions?: CustomOnboardingAction[];
  aiExtractedGender?: AIExtractedGender;
  onboardingMarketingCapture?: Record<string, string>;
  creatorNiche?: CreatorNiche;

  theme?: {
    themeId: string;
    appliedAt: AppDate;
  } & Omit<
    PopStoreThemeTemplate,
    | 'createdByProfile'
    | 'createdByUserId'
    | 'moderationStatus'
    | 'moderatedAt'
    | 'moderatorProfile'
    | 'moderatorUserId'
  >;

  // default pay to view price for post
  defaultPostPayToViewPrice?: number;

  // default subscription level for post
  defaultPostSubscriptionLevel?: PopStoreExtendedSubscriptionLevel;

  // default target amount for post group tipping
  defaultPostGroupTippingTargetAmount?: number;

  bootstrappedFrom?: {
    urlCode: string;
    name: string;
    aboutUrl?: string;
    instagramUrl?: string;
    tiktokUrl?: string;
    youtubeUrl?: string;
    patreonUrl?: string;
    snapchatUrl?: string;
    linkInBioUrl?: string;
    popStoreUrl?: string;
    seriesName?: string;
    videoUrls?: string[];
  };
  lastestPaymentMethod?: {
    paymentMethodId?: string;
    stripeConfirmationToken?: string;
    currency?: string;
    paymentMethodDetails?: PaymentMethodDetails;
    createdAt: AppDate;
  };
  bedrock?: {
    enabled?: boolean;
  };
  popStoreIndependentDomain?: {
    domainName: string;
    setupStatus:
      | 'connecting'
      | 'connected'
      | 'error-connect'
      | 'disconnecting'
      | 'disconnected'
      | 'error-disconnect';
    postSetupStatus?: 'succeeded' | 'failed';
    connectingAt?: AppDate;
    connectedAt?: AppDate;
    disconnectedAt?: AppDate;
    setupError?: string;
    postSetupError?: string;
  };
  // seller's instagram & tiktok info
  instagramFollowerCount?: number;
  instagramIsVerified?: boolean;
  instagramLinkInBio?: string;
  instagramProfileUrl?: string;
  tiktokFollowerCount?: number;
  tiktokIsVerified?: boolean;
  tiktokLinkInBio?: string;
  youtubeFollowerCount?: number;
  youtubeIsVerified?: boolean;
  youtubeLinkInBio?: string;
  receivePersonalizedOffers?: boolean;
  zapier?: ZapierIntegration;

  profileTemplateTypeValue?: string; // value in ProfileTemplateType

  /**
   * Which dashboard shell the creator last switched to. See
   * {@link PopStoreCreatorDashboardShellMode}.
   */
  dashboardShellMode?: PopStoreCreatorDashboardShellMode;
}

export enum ZapierConnectionStatus {
  Authorized = 'authorized',
  Expired = 'expired',
  Error = 'error',
}
interface ZapierIntegration {
  connectionStatus: ZapierConnectionStatus;
  redirectUri?: string;
  tokens?: {
    accessToken: string;
    refreshToken: string;
    scope: string;
    expiryDate: AppDate;
  };
}

export interface ProfileTemplateType extends ModelBase {
  label: string;
  value: string;
}

export interface LeadsSharePhoneConsent {
  phoneConsent: boolean;
  phoneConsentTimestamp?: AppDate;
}

export interface LeadsOfferConsent {
  offerConsent: boolean;
  offerConsentTimestamp?: AppDate;
}

export enum LeadsSourceType {
  RealEstateListing = 'REAL_ESTATE_LISTING',
  DigitalProduct = 'DIGITAL_PRODUCT',
  LeadForm = 'LEAD_FORM',
  Calendar = 'CALENDAR',
  LinkButton = 'LINK_BUTTON',
  SimpleLead = 'SIMPLE_LEAD',
}

export interface LeadsSourceDetails {
  title: string;
}

export interface LeadsContact extends ModelBase {
  creatorId: string;
  creatorProfile?: BasicProfile;

  sourceType?: LeadsSourceType;
  sourceId?: string;
  // deprecated. use sourceType instead
  source?: string;
  sourceDetails?: LeadsSourceDetails;

  // contextual information
  context?: Record<string, unknown>;

  // user id if it is not anonymous
  userId?: string;

  name?: string;
  email?: string;
  phoneNumber?: string;

  sharePhoneConsent?: LeadsSharePhoneConsent;
  offerConsent?: LeadsOfferConsent;

  leadFormAnswers?: AnsweredLeadFormQuestion[];
}
